import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useQuery,
} from "@tanstack/react-query";
import { Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import { ReadonlyObject } from "@/schema/schema.ts";
import { httpChecked, queryClient } from "@/util/fetch.tsx";

const schema = ReadonlyObject({
  counts: Type.Record(Type.String(), Type.Integer()),
});

const checker = TypeCompiler.Compile(schema);

function get_values(values: readonly string[]) {
  return values
    .reduce<string[]>((memo, value) => {
      if (value) {
        return memo.concat(`values[]=${encodeURIComponent(value)}`);
      }
      return memo;
    }, [])
    .join("&");
}

interface Field {
  sectionType: string;
  fieldName: string;
}

interface FieldWithLanguage extends Field {
  languageCode: string;
}

const queries = {
  field: (field: Field) => ["number-of-users", field] as const,
  fieldWithLanguage: ({ sectionType, fieldName, ...rest }: FieldWithLanguage) =>
    [...queries.field({ sectionType, fieldName }), rest] as const,
  list: (
    field: FieldWithLanguage,
    values: readonly string[],
    customerName?: string,
  ) =>
    queryOptions({
      queryKey: [
        ...queries.fieldWithLanguage(field),
        { values },
        { customerName },
      ] as const,
      queryFn:
        field.sectionType === "custom_tags"
          ? skipToken
          : async ({ signal }) => {
              const url = new URL(
                `/api/v2/number_of_users/${field.languageCode}/${field.sectionType}/${field.fieldName}`,
                window.location.origin,
              );

              url.search = get_values(values);

              if (customerName) {
                url.searchParams.set("customer", customerName);
              }

              const { counts } = await httpChecked.get(url, checker, {
                signal,
              });

              return counts;
            },
    }),
};

export function useNumberOfUsers(
  field: FieldWithLanguage,
  values: readonly string[],
) {
  const { data } = useQuery({
    ...queries.list(field, values),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  return data;
}

export async function invalidateNumberOfUsers(field: Field) {
  await queryClient.invalidateQueries({ queryKey: queries.field(field) });
}
